import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RegisteredHoursDialog } from '../../RegisteredHoursDialog';
import { registeredHoursLabel } from '../../registered-hours';
import React, { useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Building, ClipboardExport, Clock, DocumentText, Eye, Import, Refresh, Timer1, User } from 'iconsax-react';
import { TableHeader } from '../../../../../components/common/header/tableHeader/TableHeader';
import { colCenter } from '../../../../../style/style';
import { palette } from '../../../../../../theme/palette';
import { AcceptHoursDialog } from '../../single-user-hours/dialog/AceptHoursDialog';
import { OpenTimesheetDialog } from '../../single-user-hours/dialog/OTimeShetDialog';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { useAppDispatch } from '@application/store/useStore';
import { convertBase64toBlob, getMimeTypeFromBase64 } from '@application/common/helpers/file-helper';
import { ContractEndDateExtendConfirmationDialog } from '../../single-user-hours/dialog/ContractEndDateExtendConfirmationDialog';
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import { reSyncBusinessCentralHoursByTimeSheetIdsAsync } from '@application/features/HoursRegistration/hoursRegistrationThunk';
import { SyncConfirmationDialog } from '../dialog/syncConfirmationDialog';
import { hourRegistrationErrorMessage } from '@application/features/HoursRegistration/hour-registration-error';
import { ActivityIndicator } from '@ui/components/common/micro-interactions';
// Presentation only; the existing table/loading state is unchanged.
const HoursLoadingOverlay = () => (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'rgba(255, 255, 255, 0.6)' }, children: _jsx(ActivityIndicator, { label: 'Loading hours\u2026' }) }));
const theme = palette('light');
const stagingLabels = {
    NotStaged: 'Not staged',
    Waiting: 'Waiting for feed',
    PartiallyReturned: 'Partially returned in feed',
    Returned: 'Returned in feed',
    Excluded: 'Excluded from feed',
    Unknown: 'Incomplete or unknown'
};
const canRequeue = (row) => row.businessCentralTotalCount !== undefined
    ? row.businessCentralTotalCount > 0
    : !row.businessCentralStatus && row.isTimesheetDataSyncInBC === true;
export const UserHourTable = ({ tab, onSelectionChange, timeSheetData, searchQuery, onActionComplete, fetchTimeSheetData }) => {
    const dispatch = useAppDispatch();
    const [detailRow, setDetailRow] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const islg = useMediaQuery('(max-width:800px)');
    const isXl = useMediaQuery('(max-width:1612px)');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogForContractEndDateExtendConfirmation, setDialogForContractEndDateExtendConfirmation] = useState(false);
    const [openAcceptedHourDialog, setOpenAcceptedHourDialog] = useState(false);
    const [commaSeparatedIds, setSelectedRowIds] = useState(null);
    const [selectedDeclinedRow, setSelectedDeclinedRow] = useState();
    const [selectedClientName, setSelectedClientName] = useState();
    const [selectedMonth, setSelectedMonth] = useState();
    const [confirmSync, setConfirmSync] = useState(false);
    const [selectedTimeSheetIds, setSelectedTimeSheetIds] = useState('');
    const [syncError, setSyncError] = useState(null);
    const [isRequeuing, setIsRequeuing] = useState(false);
    const requeueInFlight = useRef(false);
    const filteredData = useMemo(() => {
        if (searchQuery && searchQuery !== '') {
            return (timeSheetData || []).filter((timeSheet) => timeSheet?.freelancerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                timeSheet?.allClientNames?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return timeSheetData || [];
    }, [searchQuery, timeSheetData]);
    const columns = [
        {
            field: 'freelancerName',
            headerName: 'Name',
            flex: islg ? undefined : 1,
            width: islg ? 180 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: User, text: 'Name' }),
            renderCell: ({ row }) => islg ? _jsxs(Box, { sx: { py: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: 'body2', sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: row.freelancerName }), _jsx(Typography, { variant: 'caption', display: 'block', sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: row.allClientNames }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: row.monthName })] }) : row.freelancerName
        },
        {
            field: 'freelancerPosition',
            headerName: 'Position',
            flex: islg ? undefined : 1,
            width: islg ? 180 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText, text: 'Position' })
        },
        {
            field: 'allClientNames',
            headerName: 'Client',
            flex: islg ? undefined : 1,
            width: islg ? 200 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Building, text: 'Client' }),
            renderCell: (params) => {
                const clientName = params.row.allClientNames ?? '';
                const taskName = params.row.projectTaskName;
                return _jsx("span", { children: taskName ? `${clientName} - ${taskName}` : clientName });
            }
        },
        {
            field: 'monthName',
            headerName: 'Month',
            flex: isXl ? undefined : 0.9,
            width: isXl ? 150 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Timer1, text: 'Month' })
        },
        {
            field: 'totalHours',
            headerName: 'Registered hours',
            flex: isXl ? undefined : 0.7,
            minWidth: 155,
            width: isXl ? 155 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Clock, text: 'Registered hours' }),
            renderCell: (params) => {
                const breakdown = params.row.hoursBreakdown ?? [];
                return (_jsx(Tooltip, { arrow: true, title: _jsxs(Box, { children: [_jsxs(Typography, { variant: 'caption', display: 'block', fontWeight: 'bold', children: ["Registered hours: ", registeredHoursLabel(params.row.totalHours, params.row.hasHoursConflict)] }), breakdown.map((item) => (_jsxs(Typography, { variant: 'caption', display: 'block', children: [item.displayName || item.reasonName, ": ", item.hours] }, item.assignmentReasonId || item.reasonName)))] }), children: _jsx("span", { tabIndex: 0, children: registeredHoursLabel(params.row.totalHours, params.row.hasHoursConflict) }) }));
            }
        }
    ];
    const convertUrlFileToPDF = (fileName, folderName) => {
        const fileDto = { folderName: folderName, fileName: fileName };
        dispatch(downloadCOCDocumentAsync(fileDto))
            .unwrap()
            .then((res) => {
            const mimeType = getMimeTypeFromBase64(res.file);
            if (mimeType === 'application/pdf') {
                // Preview the PDF in a new tab
                const mimeType = getMimeTypeFromBase64(res.file);
                const pdfWindow = window.open();
                pdfWindow?.document.write(`<title>Chamber of Commerce Document</title>`);
                pdfWindow?.document.write(`<iframe width='100%' height='100%' src='data:${mimeType};base64,${encodeURI(res.file)}'></iframe>`);
            }
            else {
                const blob = convertBase64toBlob(res.file, mimeType ?? '');
                if (!blob) {
                    console.error('Failed to create Blob from the base64 string.');
                    return;
                }
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName || 'download';
                link.click();
                URL.revokeObjectURL(url);
            }
        })
            .catch(() => { });
    };
    const handleConfirmSync = async () => {
        if (requeueInFlight.current || !selectedTimeSheetIds)
            return;
        const row = timeSheetData.find((candidate) => candidate.timeSheetIds === selectedTimeSheetIds);
        if (!row || !canRequeue(row)) {
            setSyncError('No existing staging rows are available to requeue. Refresh the hours list.');
            return;
        }
        requeueInFlight.current = true;
        setIsRequeuing(true);
        setSyncError(null);
        try {
            await dispatch(reSyncBusinessCentralHoursByTimeSheetIdsAsync(selectedTimeSheetIds)).unwrap();
            fetchTimeSheetData();
            setSelectedTimeSheetIds('');
            setConfirmSync(false);
        }
        catch (error) {
            setSyncError(hourRegistrationErrorMessage(error, 'requeue these staging rows'));
        }
        finally {
            requeueInFlight.current = false;
            setIsRequeuing(false);
        }
    };
    if (tab === 1 || tab === 3) {
        columns.push({
            field: 'actions',
            headerName: 'Review',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 1,
            width: islg ? 150 : undefined,
            renderHeader: () => (_jsxs(Box, { sx: { ...colCenter, gap: 2 }, children: [_jsx(ClipboardExport, { style: { color: theme.primary.main } }), _jsx(Typography, { variant: 'subtitle2', color: 'white', children: "Actions" })] })),
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [tab === 1 && _jsx(IconButton, { "aria-label": 'Review submitted hours', disabled: !!params.row.hasHoursConflict, onClick: () => {
                            setSelectedRowIds(params.id);
                            setSelectedClientName(params.row.allClientNames);
                            setSelectedMonth(params.row.monthName);
                            if (tab === 1) {
                                handleOpenDialog();
                            }
                            else {
                                setDetailRow(params.row);
                            }
                        }, children: _jsx(Eye, {}) }), tab === 3 && params.row.assignmentStatus == ClientContractStatus.Expired && (_jsx(Tooltip, { title: 'Extend deadline', children: _jsx(IconButton, { onClick: () => {
                                setSelectedDeclinedRow(params.row);
                                setDialogForContractEndDateExtendConfirmation(true);
                            }, children: _jsx(Clock, {}) }) }))] }))
        });
    }
    else if (tab === 2) {
        columns.push({
            field: 'businessCentralStatus',
            headerName: 'BC staging',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                const row = params.row;
                const label = row.businessCentralStatus ? stagingLabels[row.businessCentralStatus] || 'Unknown' : 'Status unavailable';
                return (_jsx(Tooltip, { arrow: true, title: _jsxs(Box, { children: [row.businessCentralStatus ? (_jsxs(Typography, { variant: 'caption', display: 'block', children: ["Waiting: ", row.businessCentralWaitingCount ?? 'unknown', " \u00B7 Returned: ", row.businessCentralReturnedCount ?? 'unknown', " \u00B7 Excluded: ", row.businessCentralExcludedCount ?? 'unknown', " \u00B7 Unknown: ", row.businessCentralUnknownCount ?? 'unknown', " \u00B7 Total staged: ", row.businessCentralTotalCount ?? 'unknown', " \u00B7 Missing: ", row.businessCentralMissingCount ?? 'unknown'] })) : _jsx(Typography, { variant: 'caption', display: 'block', children: "Detailed staging status is not available from this API version." }), _jsx(Typography, { variant: 'caption', display: 'block', children: "Returned means included in a feed response. Business Central import or posting is not confirmed." })] }), children: _jsx("span", { tabIndex: 0, children: label }) }));
            }
        });
        columns.push({
            field: 'action',
            headerName: 'Action',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 0.8,
            width: islg ? 100 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: ClipboardExport, text: 'Actions' }),
            renderCell: (params) => (_jsx(IconButton, { "aria-label": 'Download timesheet', disabled: !params.row.fileName || !params.row.folderName, onClick: () => {
                    if (params.row.fileName && params.row.folderName)
                        convertUrlFileToPDF(params.row.fileName, params.row.folderName);
                }, children: _jsx(Import, { "aria-label": 'Download timesheet' }) }))
        });
        columns.push({
            field: 'resync',
            headerName: 'Requeue',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 0.8,
            width: islg ? 100 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Refresh, text: 'Requeue' }),
            renderCell: (params) => {
                if (!canRequeue(params.row)) {
                    return null;
                }
                return (_jsx(IconButton, { "aria-label": 'Requeue existing staging rows', disabled: isRequeuing, onClick: () => {
                        setSelectedTimeSheetIds(params.row.timeSheetIds);
                        setSyncError(null);
                        setConfirmSync(true);
                    }, children: _jsx(Refresh, {}) }));
            }
        });
    }
    columns.push({
        field: 'registeredHoursDetail', headerName: 'Details', width: 90, sortable: false,
        renderCell: ({ row }) => _jsx(Tooltip, { title: 'View registered hours', children: _jsx("span", { children: _jsx(IconButton, { "aria-label": `View registered hours for ${row.freelancerName}, ${row.monthName}`, disabled: !!row.hasHoursConflict, onClick: () => setDetailRow(row), children: _jsx(Eye, {}) }) }) })
    });
    const getRowClassName = (params) => {
        const rowId = params.id;
        if (selectedRows.includes(rowId)) {
            return 'selected-row';
        }
        return '';
    };
    const handleOpenDialog = () => {
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    const closePopupForContractEndDateConfirmation = () => {
        setDialogForContractEndDateExtendConfirmation(false);
    };
    const handleOpenReadonlyDialog = () => {
        setOpenAcceptedHourDialog(true);
    };
    const handleCloseReadonlyDialog = () => {
        setOpenAcceptedHourDialog(false);
    };
    return (_jsxs("div", { style: {
            width: '100%',
            height: 'calc(100svh - 300px)',
            overflow: 'auto',
            background: 'white',
            borderRadius: '25px'
        }, children: [_jsx(DataGrid, { slots: { loadingOverlay: HoursLoadingOverlay }, sx: {
                    borderRadius: '25px',
                    height: 'calc(100svh - 300px)',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: theme.black.main,
                        color: '#ffffff',
                        borderRadius: '25px',
                        marginBottom: '15px'
                    },
                    '& .MuiDataGrid-cell': {
                        border: 'none',
                        paddingLeft: '16px',
                        paddingRight: '16px'
                    },
                    '& .MuiDataGrid-row': {
                        background: 'white !important'
                    },
                    '& .MuiDataGrid-row.selected-row': {
                        background: '#FFF7E0 !important'
                    },
                    '& .MuiDataGrid-main': {
                        background: theme.background.neutral
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        background: 'white'
                    },
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none'
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none'
                    }
                }, rows: filteredData.map((data) => ({
                    ...data,
                    id: data.timeSheetIds
                })), columnVisibilityModel: { freelancerPosition: !islg, allClientNames: !islg, monthName: !islg }, rowHeight: islg ? 88 : 52, columns: [
                    ...['freelancerName', 'totalHours', 'registeredHoursDetail'].map(field => columns.find(column => column.field === field)),
                    ...columns.filter(column => !['freelancerName', 'totalHours', 'registeredHoursDetail'].includes(column.field))
                ].map((column) => ({
                    ...column,
                    ...(islg && column.field === 'freelancerName' ? { width: 160 } : {}),
                    ...(islg && column.field === 'totalHours' ? { headerName: 'Hours', renderHeader: () => _jsx("span", { children: "Hours" }), width: 100, minWidth: 100, flex: undefined } : {}),
                    ...(islg && column.field === 'registeredHoursDetail' ? { width: 70 } : {}),
                    sortable: false
                })), hideFooterPagination: true, hideFooterSelectedRowCount: true, hideFooter: true, disableColumnMenu: true, getRowClassName: getRowClassName }), _jsx(RegisteredHoursDialog, { row: detailRow, onClose: () => setDetailRow(null) }), _jsx(OpenTimesheetDialog, { open: openDialog, handleClose: handleCloseDialog, timeSheetIds: commaSeparatedIds, onActionComplete: onActionComplete, clientName: selectedClientName, monthAndYear: selectedMonth }), _jsx(AcceptHoursDialog, { open: openAcceptedHourDialog, handleClose: handleCloseReadonlyDialog, timeSheetIds: commaSeparatedIds, clientName: selectedClientName, monthAndYear: selectedMonth }), _jsx(ContractEndDateExtendConfirmationDialog, { open: dialogForContractEndDateExtendConfirmation, handleClose: closePopupForContractEndDateConfirmation, selectedRow: selectedDeclinedRow }), _jsx(SyncConfirmationDialog, { open: confirmSync, handleClose: () => {
                    if (isRequeuing)
                        return;
                    setConfirmSync(false);
                    setSelectedTimeSheetIds('');
                }, handleSubmit: handleConfirmSync, pending: isRequeuing, error: syncError })] }));
};
export default UserHourTable;
